<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">{{ $t("pos.menu.report_promotions") }}</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
				<div class="col-lg-6 col-5 text-right">
					<base-button size="md" type="neutral" :disabled="loading" @click="exportXlsx">{{ $t("pos.common.export") }}</base-button>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<pos-install-alert :show="notInstalled"></pos-install-alert>
			<card class="no-border-card">
				<pos-report-filter v-model="filter" @search="load"></pos-report-filter>
			</card>

			<!-- phase 3 (R-U): the bundles section is gone; special prices are on POS products -->
			<!-- table edge-to-edge in the card (as /backoffice/orders-overview): card body px-0, everything else in px-4 -->
			<card v-loading="loading" body-classes="px-0">
				<h4 class="mb-3 px-4">{{ $t("pos.report.section_special") }}</h4>
				<el-table :data="data.special_prices" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.report.product')" prop="name" min-width="200"></el-table-column>
					<el-table-column :label="$t('pos.report.event')" prop="event_name" min-width="160"></el-table-column>
					<el-table-column :label="$t('pos.report.qty')" prop="qty" width="100" align="right"></el-table-column>
					<el-table-column :label="$t('pos.report.amount')" min-width="120" align="right">
						<template slot-scope="{ row }">{{ money(row.amount) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.retail_value')" min-width="130" align="right">
						<template slot-scope="{ row }">{{ money(row.retail_value) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.discount_given')" min-width="130" align="right">
						<template slot-scope="{ row }">{{ money(row.discount_given) }}</template>
					</el-table-column>
				</el-table>
			</card>

			<card body-classes="px-0">
				<h4 class="mb-3 px-4">{{ $t("pos.report.section_discount") }}</h4>
				<div class="px-4">
					<div class="row text-center mb-3">
						<div class="col"><div class="text-muted small">{{ $t("pos.report.bill_count") }}</div><div class="h3 mb-0">{{ int(disc.bill_count) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.report.discount_total") }}</div><div class="h3 mb-0">{{ money(disc.discount_total) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.report.approved_count") }}</div><div class="h3 mb-0">{{ int(disc.approved_count) }}</div></div>
						<div class="col"><div class="text-muted small">{{ $t("pos.report.approved_total") }}</div><div class="h3 mb-0">{{ money(disc.approved_total) }}</div></div>
					</div>
				</div>
				<el-table :data="disc.by_event || []" header-row-class-name="thead-light" class="bon-table pos-table">
					<el-table-column :label="$t('pos.report.event')" min-width="200">
						<template slot-scope="{ row }">{{ eventLabel(row) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.bill_count')" prop="bill_count" width="110" align="right"></el-table-column>
					<el-table-column :label="$t('pos.report.discount_total')" min-width="130" align="right">
						<template slot-scope="{ row }">{{ money(row.discount_total) }}</template>
					</el-table-column>
					<el-table-column :label="$t('pos.report.approved_count')" min-width="150" align="right" prop="approved_count"></el-table-column>
				</el-table>
			</card>
		</div>
	</div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { PosService } from "@/resource";
import { posMoney, posInt, posEventLabel } from "@/utils/pos";
import posPageMixin, { posDefaultFilter } from "./posPageMixin";
import PosReportFilter from "./PosReportFilter.vue";
import PosInstallAlert from "./PosInstallAlert.vue";

export default {
	name: "PosReportPromotions",
	mixins: [posPageMixin],
	components: { RouteBreadCrumb, PosReportFilter, PosInstallAlert, [Table.name]: Table, [TableColumn.name]: TableColumn },
	data() {
		return { filter: posDefaultFilter(), data: { special_prices: [], bill_discounts: {} }, lastParams: null };
	},
	computed: {
		disc() {
			return this.data.bill_discounts || {};
		}
	},
	mounted() {
		this.load();
	},
	methods: {
		money: posMoney,
		int: posInt,
		eventLabel(r) {
			return posEventLabel(r.event_id, r.event_name, (k) => this.$t(k));
		},
		async load() {
			const p = this.posParams(this.filter);
			if (!p) return;
			this.loading = true;
			try {
				const res = await PosService.PosReportPromotions(p);
				const d = (res.data && res.data.Datum) || {};
				this.data = { special_prices: d.special_prices || [], bill_discounts: d.bill_discounts || {} };
				this.lastParams = p;
				this.notInstalled = false;
			} catch (err) {
				this.posError(err);
			} finally {
				this.loading = false;
			}
		},
		exportXlsx() {
			const p = this.lastParams || this.posParams(this.filter);
			if (!p) return;
			const t = (k) => this.$t(k);
			this.posExport("promotions", p, [
				{
					name: t("pos.report.section_special"),
					rows: this.data.special_prices,
					columns: [
						{ label: t("pos.report.product"), value: "name" },
						{ label: t("pos.report.event"), value: "event_name" },
						{ label: t("pos.report.qty"), value: "qty" },
						{ label: t("pos.report.amount"), value: (r) => Number(r.amount) },
						{ label: t("pos.report.retail_value"), value: (r) => Number(r.retail_value) },
						{ label: t("pos.report.discount_given"), value: (r) => Number(r.discount_given) }
					]
				},
				{
					name: t("pos.report.section_discount"),
					rows: this.disc.by_event || [],
					columns: [
						{ label: t("pos.report.event"), value: (r) => this.eventLabel(r) },
						{ label: t("pos.report.bill_count"), value: "bill_count" },
						{ label: t("pos.report.discount_total"), value: (r) => Number(r.discount_total) },
						{ label: t("pos.report.approved_count"), value: "approved_count" },
						{ label: t("pos.report.approved_total"), value: (r) => Number(r.approved_total) }
					]
				}
			]);
		}
	}
};
</script>
